import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import {
  useContentCategoriesMap,
  Link,
  LocalLabelsProvider,
} from '@sentecacommerce-theme/lib'
import { Typography, Modal, IconChevronRight, BlogMobileMenu } from '../Common'
import {
  BlogPageProps,
  Category,
  CategoryExtraFields,
  BlogPageLabels,
} from './blogPage.props'
import { BlogPost, BlogListing as BlogListingComponent } from './Components'

export const BlogPage = ({
  labels,
  categories,
  defaultMetaDescription,
}: BlogPageProps) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const router = useRouter()

  const categoriesMap = useContentCategoriesMap<CategoryExtraFields>(
    categories?.categoriesList
  )

  const { slug } = router?.query as any

  const [title, setTitle] = useState(
    categoriesMap[slug]?.label || labels?.title
  )

  const isIndexPage = router.pathname === '/blog'
  const isCategoryPage = router.pathname === '/blog/[slug]/c'
  const isProductPage = router.pathname === '/blog/[slug]'

  useEffect(() => {
    if (isCategoryPage) {
      setTitle(categoriesMap[slug]?.label)
    } else if (isIndexPage) {
      setTitle(labels?.title)
    }
    if (isCategoryPage && !categoriesMap[slug]) {
      router.push('/404')
    }
  }, [slug, isCategoryPage, isIndexPage, categoriesMap, labels, router])

  const closeModal = () => setShowMobileMenu(false)
  const openModal = () => setShowMobileMenu(true)

  return (
    <LocalLabelsProvider<BlogPageLabels> labels={labels}>
      <MainContainer>
        <ContentFlexContainer>
          <Aside>
            <CategoriesHeader>
              <UnderlineTitle variant="7">{categories.title}</UnderlineTitle>
            </CategoriesHeader>
            <CategoriesContainer>
              {categories?.categoriesList?.map((el: Category) => {
                return (
                  <Link href={`/blog/${el?.slug}/c`} key={el?.slug}>
                    <CategoryName>{el?.label}</CategoryName>
                  </Link>
                )
              })}
            </CategoriesContainer>
          </Aside>
          <BlogListing>
            <TitleAndIconsContainer>
              <StyledRightArrow
                onClick={() => (isIndexPage ? router.push('/') : router.back())}
              />
              <BlogTitle as="h1">{title}</BlogTitle>
              <div className="ml-3">
                <BlogMobileMenu
                  style={{ width: '2.3rem' }}
                  onClick={openModal}
                />
              </div>
            </TitleAndIconsContainer>
            {(isIndexPage || isCategoryPage) && (
              <BlogListingComponent
                categoriesList={categories?.categoriesList}
                title={title}
                defaultMetaDescription={defaultMetaDescription}
              />
            )}
            {isProductPage && (
              <BlogPost
                setTitle={setTitle}
                defaultMetaDescription={defaultMetaDescription}
              />
            )}
          </BlogListing>
          {showMobileMenu && (
            <Modal
              open={showMobileMenu}
              handleClose={closeModal}
              full
              noTitlePadding
              noContentPadding
            >
              <ModalHeader>{categories.title}</ModalHeader>
              <div className="px-4">
                {categories?.categoriesList?.map((el: Category) => (
                  <Link href={`/blog/${el?.slug}/c`} key={el?.slug}>
                    <MobileCatName
                      bold={el.label === title}
                      onClick={closeModal}
                    >
                      {el?.label}
                    </MobileCatName>
                  </Link>
                ))}
              </div>
            </Modal>
          )}
        </ContentFlexContainer>
      </MainContainer>
    </LocalLabelsProvider>
  )
}

const MainContainer = styled.div.attrs({
  className: 'container',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
`

const ContentFlexContainer = styled.div.attrs({
  className: 'd-flex align-items-start justify-content-center ',
})`
  flex-direction: column;

  margin-top: 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: row;

    margin-top: 3rem;
  }
`

//  Blog

const BlogTitle = styled(Typography).attrs({
  variant: 3,
  // className: 'mb-3',
})`
  font-weight: normal;
  line-height: normal;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    font-size: 2.8rem;
  }
`

const BlogItemsContainer = styled.div.attrs({
  className: '',
})``

const BlogListing = styled.div.attrs({
  className: 'd-flex flex-column align-items-start',
})`
  flex-basis: 80%;
  width: 100%;

  ${BlogItemsContainer} + ${BlogItemsContainer} {
    margin-top: 1rem;
  }
`

//  Aside

const UnderlineTitle = styled(Typography)`
  color: inherit;
  font-weight: bold;
  text-transform: uppercase;
  position: relative;

  &:after {
    content: '';
    background-color: ${({ theme }) => theme.palette.greyTitle};
    position: absolute;
    bottom: -1rem;
    left: 0;
    width: 100%;
    height: 0.3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    font-size: 1.6rem;
  }
`

const CategoryName = styled(Typography).attrs({
  variant: '8',
  className: 'py-3 pl-1',
})`
  width: 100%;
  cursor: pointer;
  flex-basis: 33.333%;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    text-decoration: none;
    font-size: 1.5rem;
    &:hover {
      transition: background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      background-color: ${({ theme }) => theme.palette.lightgrey};
    }
  }
`

const CategoriesHeader = styled.div.attrs({
  className: 'd-none d-lg-flex mb-4',
})``

const CategoriesContainer = styled.div.attrs({
  className: 'd-flex justify-content-center',
})`
  flex-direction: row;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: column;
  }
`

const Aside = styled.div.attrs({
  className:
    'd-none d-lg-flex flex-column align-items-start justify-content-center ',
})`
  flex-basis: 20%;
  margin-right: 1rem;
  margin-top: 1.5rem;
`
const TitleAndIconsContainer = styled.div.attrs({
  className: 'd-flex align-items-center mb-4 mb-lg-5 ',
})`
  width: 100%;
  justify-content: space-between;

  & svg {
    height: 100%;
    width: 2rem;
    color: ${({ theme }) => theme.palette.greyTitle};
    fill: ${({ theme }) => theme.palette.greyTitle};
    @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
      display: none;
      padding-top: 3rem !important;
    }
  }
`
const StyledRightArrow = styled(IconChevronRight).attrs({
  className: 'mr-3',
})`
  transform: rotate(180deg);
  width: 1.5rem !important;
`
const ModalHeader = styled(Typography).attrs({
  color: 'primary',
  variant: 3,
  as: 'p',
  className: 'mb-4 py-3 px-4',
  'data-blogger': '',
})`
  text-align: center;
  background: ${({ theme }) => theme.palette.panache};
  color: ${({ theme }) => theme.palette.greyTitle};
  top: 0;
  left: 0;
  width: 100%;
`
const MobileCatName = styled(CategoryName).attrs({ variant: '5' })<{
  bold: boolean
}>`
  color: ${({ theme }) => theme.palette.greyTitle};
  ${(props) =>
    props.bold &&
    css`
      font-weight: 550;
    `}
`
export const MobileTitle = styled(Typography).attrs({
  variant: '6',
  className: 'mb-3',
})`
  font-weight: bold;
  text-transform: uppercase;
  position: relative;

  &:after {
    content: '';
    background-color: ${({ theme }) => theme.palette.silverTree};
    position: absolute;
    bottom: -1rem;
    left: 0;
    width: 100%;
    height: 0.3rem;
    display: inline-block;
  }
`

export const Header = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-between mb-4',
})`
  position: relative;
  color: ${({ theme }) => theme.palette.silverTree};
  border-bottom: 0.1rem solid rgba(88, 181, 130, 0.6);
`
